import { Separator } from "~/components/ui/separator";
import {
    ChartTypes,
    TimeSeriesUnits,
    TimeSeriesView,
} from "~/modules/token_timeseries/types";
import { YAxisTypes } from "~/modules/visualization/components/Timeseries/types";
import { AssetScreenerListingDualAxisChart } from "./AssetScreenerListingDualAxisChart";
import { usePerformanceChartData } from "./usePerformanceChartData";
import type { ScreenerAsset } from "./types";
import { getPerformanceSummaryRows } from "./assetPerformance";

// // // //

function PerformanceSummaryTable(props: {
    rows: Array<{ label: string; formattedValue: string; detail?: string }>;
}) {
    const { rows } = props;

    return (
        <div className="mt-4 w-full max-w-lg">
            <Separator className="mb-3 bg-secondary" />
            <table className="w-full table-fixed text-sm">
                <thead>
                    <tr>
                        {rows.map((row) => (
                            <th
                                key={row.label}
                                scope="col"
                                className="whitespace-nowrap pb-1 pr-2 text-left text-xs font-normal text-tertiary-foreground"
                            >
                                {row.label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        {rows.map((row) => (
                            <td
                                key={row.label}
                                className="whitespace-nowrap pr-2 text-left align-top font-medium tabular-nums text-foreground"
                            >
                                {row.formattedValue}
                                {row.detail && (
                                    <p className="mt-0.5 text-[11px] font-normal leading-none text-tertiary-foreground">
                                        {row.detail}
                                    </p>
                                )}
                            </td>
                        ))}
                    </tr>
                </tbody>
            </table>
        </div>
    );
}

// // // //

export function HypotheticalPerformanceChart(props: { asset: ScreenerAsset }) {
    const { asset } = props;

    const { chartData, isLoading } = usePerformanceChartData(asset.id);

    const performanceRows = getPerformanceSummaryRows(asset);

    const series: Array<TimeSeriesView & { yAxis?: 0 | 1 }> = [
        ...(chartData
            ? [
                  {
                      item: {
                          ...chartData,
                          measure: {
                              ...chartData.measure,
                              unit: TimeSeriesUnits.dollar,
                          },
                          group: {
                              ...chartData.group,
                              name: "Growth of $10,000",
                              color: "#334155",
                          },
                      },
                      enabled: true,
                      chartType: ChartTypes.area as const,
                      yAxis: 0 as const,
                  },
              ]
            : []),
    ];

    return (
        <>
            {isLoading && (
                <p className="text-sm text-tertiary-foreground">
                    Loading chart...
                </p>
            )}
            {!isLoading && series.length > 0 && (
                <AssetScreenerListingDualAxisChart
                    series={series}
                    area={false}
                    isDualAxis
                    height={320}
                    yAxis={{
                        primary: {
                            min: undefined,
                            enabled: true,
                            type: YAxisTypes.linear,
                            title: "",
                            unit: TimeSeriesUnits.dollar,
                            format: "${value}",
                        },
                        secondary: {
                            min: undefined,
                            enabled: false,
                            type: YAxisTypes.linear,
                            title: "",
                            unit: TimeSeriesUnits.dollar,
                            opposite: true,
                        },
                    }}
                    legend={{
                        enabled: false,
                        itemWidth: 90,
                        align: "left",
                        verticalAlign: "top",
                    }}
                    navigator={{
                        enabled: false,
                        active: false,
                    }}
                />
            )}
            {!isLoading && series.length === 0 && (
                <p className="text-sm text-tertiary-foreground">
                    No hypothetical performance history available for{" "}
                    {asset.name}.
                </p>
            )}
            {!isLoading && performanceRows.length > 0 && (
                <PerformanceSummaryTable rows={performanceRows} />
            )}
        </>
    );
}
